import { type RefObject, useEffect } from 'react';

type Options = {
    readonly active: boolean;
    readonly containerRef: RefObject<HTMLElement | null>;
    readonly onDismiss: () => void;
    /**
     * Optional predicate to treat extra DOM subtrees (e.g. Radix-portaled
     * popovers/dialogs) as extensions of the container. Returning `true`
     * suppresses dismissal for clicks inside that subtree.
     */
    readonly isInsideAllowedExtension?: (target: Element) => boolean;
};

/**
 * Calls `onDismiss` when a mousedown lands outside the container. Listens on
 * `mousedown` (not `click`) so we react before focus reshuffles and so
 * text-selection drags that start outside don't trigger dismissal on release.
 */
export const useDismissOnOutsideMousedown = ({
    active,
    containerRef,
    isInsideAllowedExtension,
    onDismiss,
}: Options): void => {
    useEffect(() => {
        if (!active) {
            return;
        }

        const handleMouseDown = (event: MouseEvent): void => {
            const target = event.target as Node | null;

            if (target === null) {
                return;
            }

            if (containerRef.current?.contains(target) === true) {
                return;
            }

            if (target instanceof Element && isInsideAllowedExtension?.(target) === true) {
                return;
            }

            onDismiss();
        };

        document.addEventListener('mousedown', handleMouseDown);

        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [active, containerRef, isInsideAllowedExtension, onDismiss]);
};
